import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Award, CheckCircle2, Clock, Search, ShieldX, XCircle } from 'lucide-react';
import { certificadoService } from '../../shared/services';

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  return new Date(valor).toLocaleDateString('pt-BR');
}

function formatarCarga(minutos: number) {
  if (!minutos || minutos <= 0) return '—';
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  if (horas === 0) return `${resto} minutos`;
  return resto === 0 ? `${horas} hora(s)` : `${horas} hora(s) e ${resto} minuto(s)`;
}

export function ValidateCertificatePage() {
  const { codigo } = useParams<{ codigo: string }>();
  const [entrada, setEntrada] = useState(codigo ?? '');
  const [consultado, setConsultado] = useState((codigo ?? '').trim().toUpperCase());

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['validar-certificado', consultado],
    queryFn: () => certificadoService.validarCertificado(consultado),
    enabled: consultado.length > 0,
    retry: false,
  });

  const naoEncontrado = isError && axios.isAxiosError(error) && error.response?.status === 404;
  const falhaTecnica = isError && !naoEncontrado;

  const status = data?.status ?? 'valido';
  const aprovado = data?.valido === true;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-gray-100 flex items-start justify-center p-6">
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block text-2xl font-bold text-primary-700">
            Universidade Amig3
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Validar certificado</h1>
          <p className="text-gray-600 mt-2">
            Informe o código impresso no certificado para conferir a autenticidade.
          </p>
        </div>

        <form
          className="card flex flex-col sm:flex-row gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            setConsultado(entrada.trim().toUpperCase());
          }}
        >
          <label className="sr-only" htmlFor="codigo">
            Código do certificado
          </label>
          <input
            id="codigo"
            value={entrada}
            onChange={(event) => setEntrada(event.target.value)}
            placeholder="Ex.: 7QK3M9XZ4P2N8TRV"
            maxLength={16}
            className="flex-1 border border-gray-300 rounded-lg px-4 py-3 font-mono tracking-wider uppercase focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button type="submit" className="btn-primary flex items-center justify-center gap-2 px-6 py-3">
            <Search size={18} />
            Validar
          </button>
        </form>

        <div className="mt-6">
          {isLoading && (
            <div className="card text-center py-10 text-gray-500">Consultando certificado...</div>
          )}

          {naoEncontrado && (
            <div className="card text-center py-10">
              <XCircle className="mx-auto text-red-500 mb-3" size={48} />
              <h2 className="text-xl font-semibold text-gray-900">Certificado não encontrado</h2>
              <p className="text-gray-600 mt-2">
                Confira o código digitado. Se o problema persistir, procure a Universidade Amig3.
              </p>
            </div>
          )}

          {falhaTecnica && (
            <div className="card text-center py-10 text-red-600">
              Não foi possível consultar agora. Tente novamente em instantes.
            </div>
          )}

          {data && (
            <div className="card">
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 ${aprovado ? 'bg-green-100' : 'bg-red-100'}`}>
                  {aprovado ? (
                    <CheckCircle2 className="text-green-600" size={30} />
                  ) : (
                    <ShieldX className="text-red-600" size={30} />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Situação do certificado</p>
                  <h2 className={`text-2xl font-bold ${aprovado ? 'text-green-700' : 'text-red-700'}`}>
                    {aprovado ? 'Certificado válido' : status === 'expirado' ? 'Certificado expirado' : 'Certificado revogado'}
                  </h2>
                  {status === 'expirado' && (
                    <p className="text-sm text-amber-700 mt-1 flex items-center gap-1">
                      <Clock size={14} /> A validade terminou em {formatarData(data.validoAte)}.
                    </p>
                  )}
                  {status === 'revogado' && (
                    <p className="text-sm text-red-700 mt-1">
                      Este certificado foi revogado pela instituição e não deve ser aceito.
                    </p>
                  )}
                </div>
              </div>

              <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500">Titular</dt>
                  <dd className="font-semibold text-gray-900">{data.titular}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">{data.tipo === 'trilha' ? 'Trilha' : 'Curso'}</dt>
                  <dd className="font-semibold text-gray-900">{data.titulo}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Carga horária</dt>
                  <dd className="text-gray-900">{formatarCarga(data.cargaHorariaMinutos)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Código</dt>
                  <dd className="font-mono text-gray-900">{data.codigo}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Emitido em</dt>
                  <dd className="text-gray-900">{formatarData(data.emitidoEm)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Válido até</dt>
                  <dd className="text-gray-900">{formatarData(data.validoAte)}</dd>
                </div>
              </dl>

              <div className="mt-6 pt-4 border-t border-gray-200 flex items-center gap-2 text-sm text-gray-500">
                <Award size={16} />
                <span>{data.instituicao}</span>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-gray-500 mt-8">
          A validação mostra apenas os dados necessários para conferir o certificado.
        </p>
      </div>
    </div>
  );
}
